import { memo } from 'react';
import Image from 'components/Image';
import codify from 'utils/codify';
import styles from './EffectsBanner.module.scss';

const ingredientBlacklistCategories = new Set(['flower', 'pre-rolls']);

const EffectsBanner = memo(
  ({
    backgroundImageUrl,
    badgeImageUrl,
    description,
    effect,
    effectImageUrl,
    ingredientsLabel,
    ingredients,
    showIngredients,
    smallBackgroundImageUrl,
    subtitle,
    title,
  }) => {
    return (
      <div className="position-relative" data-value="effect-banner">
        <div className="d-none d-sm-block h-100 overflow-hidden position-absolute w-100">
          <Image
            fill
            alt={`${effect} background`}
            sizes="(max-width: 992px) 992w, (max-width: 1200px) 1200w, 1500w"
            src={backgroundImageUrl}
            role="presentation"
          />
        </div>
        <div className="d-block d-sm-none h-100 overflow-hidden position-absolute w-100">
          <Image
            fill
            alt={`${effect} background`}
            sizes="576w"
            src={smallBackgroundImageUrl || backgroundImageUrl}
            role="presentation"
          />
        </div>
        <div className="d-flex flex-column flex-sm-row position-relative">
          <div className="my-sm-auto p-10 p-md-17 p-lg-25 p-xl-32 w-100 w-sm-50 position-relative">
            <Image
              alt={effect}
              className="w-100 h-auto"
              height={582}
              role="presentation"
              src={effectImageUrl}
              width={582}
            />
          </div>
          <div
            className={`d-flex flex-column p-10 pb-20 pb-sm-10 my-sm-auto ps-md-17 ps-lg-25 ps-xl-32 pe-sm-9 pe-md-25 pe-lg-32 pe-xl-54 w-100 w-sm-50 ${styles.text_wrapper}`}
          >
            <div className="d-flex flex-row mb-12">
              {badgeImageUrl ? (
                <div
                  className={`position-relative me-10 ${styles.badge_wrapper}`}
                >
                  <Image
                    fill
                    alt={`Badge for ${effect} effect`}
                    className={styles.image}
                    src={badgeImageUrl}
                  />
                </div>
              ) : null}
              <div className="my-auto">
                <h2
                  className={`font-sofia fs-7 fs-lg-8 fw-700 lh-1 mb-0 text-primary text-uppercase ${styles.subtitle}`}
                >
                  {subtitle}
                </h2>
                <h3 className="font-frl fs-22 fs-lg-36 fw-900 lh-1 mb-0 text-primary">
                  {title}
                </h3>
              </div>
            </div>
            <p className="fs-7 fs-lg-9 text-primary">{description}</p>
            {showIngredients ? (
              <p className="fs-7 fs-lg-9 text-primary">
                <strong className="fs-600">{`${ingredientsLabel}:`}</strong>
                {` ${ingredients}`}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
);

export default function EffectsBannerWrapper({
  badgeImages,
  category,
  collection,
  effect,
  effectsBanners,
}) {
  if (!effectsBanners) {
    return null;
  }
  const codifiedValue = codify(collection || effect);

  const banner = effectsBanners.find(
    ({ title }) => codify(title) === codifiedValue
  );

  if (!banner) {
    return null;
  }

  const badgeImage = badgeImages.find(
    ({ title }) => title === `badge-${codifiedValue}.svg`
  );

  const {
    background_image: backgroundImage,
    description,
    effect_image: effectImage,
    ingredients_label: ingredientsLabel,
    ingredients,
    mobile_background_image: smallBackgroundImage,
    subtitle,
    title,
  } = banner;

  const showIngredients =
    ingredientsLabel && !ingredientBlacklistCategories.has(category);

  return (
    <section className="mb-14 mb-xl-25">
      <EffectsBanner
        backgroundImageUrl={backgroundImage?.url}
        badgeImageUrl={badgeImage?.url}
        description={description}
        effect={collection || effect}
        effectImageUrl={effectImage?.url}
        ingredientsLabel={ingredientsLabel}
        ingredients={ingredients}
        showIngredients={showIngredients}
        smallBackgroundImageUrl={smallBackgroundImage?.url}
        subtitle={subtitle}
        title={title}
      />
    </section>
  );
}
